import { Button, type ButtonProps, cn } from '@seen/ui'
import { IconPlus, IconSearch } from '@tabler/icons-react'
import { cva } from 'class-variance-authority'
import Fuse from 'fuse.js'
import {
  type FocusEvent,
  type FocusEventHandler,
  type ForwardedRef,
  isValidElement,
  type JSX,
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import {
  PopoverContent,
  type PopoverContentProps,
} from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { forwardRef } from '#src/utils/forwardRef.ts'

export interface NormalizedOption<V extends string = string> {
  value: V
  label: ReactNode
  trigger?: ReactNode
  keywords?: string[]
}

export type Option<V extends string = string> = NormalizedOption<V> | V

export type ValueFromOption<T extends Option> = T extends { value: infer V }
  ? V
  : T

export interface BaseComboboxProps<T extends Option> {
  options: T[]
  onBlur?: FocusEventHandler<HTMLButtonElement | HTMLDivElement>
  align?: PopoverContentProps['align']
  variant?: ButtonProps['variant']
  name?: ButtonProps['name']
  disabled?: ButtonProps['disabled']
  loading?: ButtonProps['loading']
  color?: ButtonProps['color']
  className?: ButtonProps['className']
  disableSearch?: boolean
  isPlaceholder?: boolean
  // Return true if the combobox should close after the option is selected
  onSelect?: (value: ValueFromOption<T>) => boolean
  renderTrigger: () => ReactNode
  renderItem: (props: {
    option: NormalizedOption<ValueFromOption<T>>
  }) => ReactNode
  createable?: boolean
  onCreate?: () => void
}

const variants = cva('group h-auto justify-between text-nowrap text-left', {
  variants: {
    variant: {
      default: 'data-[state=open]:brightness-95',
      outline: 'data-[state=open]:backdrop-brightness-95',
      underline: 'data-[state=open]:border-zinc-950',
    },
    placeholder: {
      true: 'text-placeholder',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    placeholder: false,
  },
})

function getTextFromReactNode(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return node.toString()
  }
  if (Array.isArray(node)) {
    return node
      .map(getTextFromReactNode)
      .filter((text) => !!text)
      .join(' ')
  }
  if (isValidElement(node)) {
    const element = node as ReactElement<{ children?: ReactNode }>
    if (element.props.children) {
      return getTextFromReactNode(element.props.children)
    }
  }
  return '' // Handle cases like null, undefined, or other non-text nodes
}

export const BaseCombobox = forwardRef(BaseComboboxWithRef)

function BaseComboboxWithRef<T extends Option>(
  {
    options,
    variant = 'outline',
    onBlur,
    align = 'start',
    loading,
    disabled,
    name,
    className,
    color,
    disableSearch,
    isPlaceholder,
    renderTrigger,
    renderItem,
    onSelect,
    createable,
    onCreate,
  }: BaseComboboxProps<T>,
  ref: ForwardedRef<HTMLButtonElement>
): JSX.Element {
  const [open, setOpen] = useState(false)

  const normalizedOptions = useMemo(
    () =>
      options.map((o) => {
        const normalizedOption =
          typeof o === 'string' ? { value: o, label: o } : o
        return normalizedOption as NormalizedOption<ValueFromOption<T>>
      }),
    [options]
  )

  const optionsWithSearchTextFromLabel = useMemo(
    () =>
      normalizedOptions.map((o) => ({
        ...o,
        searchTextFromLabel: [
          getTextFromReactNode(o.label),
          ...(o.keywords ?? []),
        ].join(' '),
      })),
    [normalizedOptions]
  )

  const [input, setInput] = useState('')
  const sortedOptions = useMemo(() => {
    if (!input) {
      return optionsWithSearchTextFromLabel
    }
    const fuse = new Fuse(optionsWithSearchTextFromLabel, {
      keys: [
        {
          name: 'searchTextFromLabel',
          getFn: (o) => o.searchTextFromLabel,
        },
      ],
    })
    const result = fuse.search(input).map((r) => r.item)
    return result
  }, [optionsWithSearchTextFromLabel, input])

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLButtonElement | HTMLDivElement>) => {
      if (!open) {
        onBlur?.(e)
      }
    },
    [onBlur, open]
  )

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setInput('')
    }
    setOpen(open)
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger onBlur={handleBlur} asChild>
        <Button
          ref={ref}
          role="combobox"
          variant={variant}
          loading={loading}
          disabled={disabled}
          name={name}
          color={color}
          className={cn(
            variants({
              variant:
                variant === 'outline' || variant === 'underline'
                  ? variant
                  : 'default',
              placeholder: isPlaceholder,
              className,
            })
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {renderTrigger()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="p-0"
        onBlur={handleBlur}
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          {!disableSearch && (
            <div className="flex items-center border-b px-3">
              <IconSearch className="mr-2 size-4 shrink-0 opacity-50" />
              <CommandInput
                // We don't use the value prop because when it's used, the list scroll position doesn't reset properly when you press backspace in the input.
                onValueChange={setInput}
                placeholder="Search..."
                className="border-none px-0"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setInput('')
                  }
                }}
              />
            </div>
          )}
          <CommandList
            className={COMBOBOX_LIST_MAX_HEIGHT}
            key={`${input}-${sortedOptions.length}`} // We use a key to reset the scroll position when the search text changes.
          >
            <CommandEmpty>None found.</CommandEmpty>
            {createable && (
              <CommandItem
                value="create"
                onSelect={() => {
                  onCreate?.()
                  setOpen(false)
                }}
                className="flex items-center gap-2"
              >
                <IconPlus size={12} /> Create new
              </CommandItem>
            )}
            {sortedOptions.slice(0, MAX_OPTIONS_TO_SHOW).map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={(selectedValue) => {
                  setOpen(
                    onSelect?.(selectedValue as ValueFromOption<T>) ?? false
                  )
                }}
                keywords={option.keywords}
              >
                {renderItem({
                  option,
                })}
              </CommandItem>
            ))}
            {sortedOptions.length > MAX_OPTIONS_TO_SHOW && (
              <CommandItem disabled>Search to see more results…</CommandItem>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const COMBOBOX_LIST_MAX_HEIGHT = 'max-h-[calc(50vh-5rem)]' // 50vh - search bar height - trigger height. 7rem is a rough estimate of the height of the search bar and trigger.

const MAX_OPTIONS_TO_SHOW = 250 // There seems to be a slowdown if it's much higher than 250 options.
